<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import NProgress from 'nprogress';
import Check from '@primeicons/vue/check';
import Download from '@primeicons/vue/download';
import EllipsisV from '@primeicons/vue/ellipsis-v';
import Link from '@primeicons/vue/link';
import Pencil from '@primeicons/vue/pencil';
import Refresh from '@primeicons/vue/refresh';
import TimesCircle from '@primeicons/vue/times-circle';
import Trash from '@primeicons/vue/trash';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import { useAutoRefresh } from '@/use/auto-refresh';
import { useShipmentDetailActions } from '@/composables/useShipmentDetailActions';
import { canEditShipmentForUser } from '@/utils/shipment-draft-access';
import { getFileName } from '@/utils/blob';
import * as events from '@/events';

const props = defineProps<{
  shipment?: V2ShipmentDetail | null;
}>();

const store = useStore();
const route = useRoute();
const menuRef = ref<InstanceType<typeof Menu> | null>(null);

const accountId = computed(() => store.getters['auth/accountId'] as number);
const id = +route.params.id;
const shipmentById = store.getters['shipment/byId'];
const shipmentFromStore = computed(() => shipmentById(id));

const actions = useShipmentDetailActions(
  () => accountId.value,
  () => props.shipment ?? null,
);

const canShowEdit = computed(() => {
  const s = props.shipment;
  if (!s || !actions.canManage.value) return false;
  return canEditShipmentForUser(
    s as V2ShipmentDetail & { archived_at?: string | null },
    true,
  );
});

const overflowMenuId = 'shipment-detail-overflow-menu';

const overflowItems = computed<MenuItem[]>(() => {
  if (!actions.canManage.value) return [];
  return [
    {
      label: 'Delete',
      command: () => actions.deleteShipment(),
    },
  ];
});

const actionButtonPt = {
  root: { class: 'shrink-0' },
};

const fetchShipmentByAccountIdAndId = (params: {
  accountId: number;
  id: number;
}) => store.dispatch('shipment/fetchByAccountIdAndId', params);

const refreshAll = () => {
  NProgress.start();
  const promise = Promise.all([
    fetchShipmentByAccountIdAndId({ accountId: accountId.value, id }),
  ]);
  promise.finally(() => {
    NProgress.done();
  });
  return promise;
};

const { enable, disable, isEnabled } = useAutoRefresh(refreshAll);

const autoRefreshTooltip = computed(() =>
  isEnabled.value ? 'Auto-refresh: On' : 'Auto-refresh: Off',
);

function toggleAutoRefresh() {
  if (isEnabled.value) {
    disable();
  } else {
    enable();
  }
}

function downloadShipment() {
  events.trigger(events.names.SHIPMENT_POINTS_DOWNLOADED);

  store.dispatch('shipment/download', {
    accountId: accountId.value,
    id,
    fileName: getFileName({
      prefix: `shipment_${shipmentFromStore.value?.name ?? 'shipment'}`,
      dateFrom: null,
      dateTo: null,
    }),
    params: {
      download: true,
      offset: new Date().getTimezoneOffset(),
    },
  });
}

function toggleOverflowMenu(event: Event) {
  menuRef.value?.toggle(event);
}
</script>

<template>
  <div class="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
    <Button
      size="small"
      severity="secondary"
      outlined
      aria-label="Download these points"
      :pt="actionButtonPt"
      v-tooltip.bottom="'Download these points'"
      @click="downloadShipment"
    >
      <template #icon>
        <Download />
      </template>
    </Button>
    <Button
      size="small"
      severity="secondary"
      outlined
      aria-label="Toggle auto-refresh"
      :pt="actionButtonPt"
      v-tooltip.bottom="autoRefreshTooltip"
      @click="toggleAutoRefresh"
    >
      <template #icon>
        <Refresh />
      </template>
    </Button>

    <template v-if="shipment">
      <Button
        size="small"
        severity="secondary"
        outlined
        :disabled="!actions.shareUrl.value"
        :pt="actionButtonPt"
        v-tooltip.bottom="'Copy share link'"
        @click="actions.shareLink()"
      >
        <Link />
        Share
      </Button>
      <Button
        v-if="canShowEdit"
        size="small"
        severity="secondary"
        outlined
        :pt="actionButtonPt"
        v-tooltip.bottom="'Edit shipment'"
        @click="actions.editShipment()"
      >
        <Pencil />
        Edit
      </Button>
      <Button
        v-if="actions.canShowPublish.value"
        size="small"
        severity="secondary"
        outlined
        :loading="actions.publishBusy.value"
        :pt="actionButtonPt"
        v-tooltip.bottom="'Publish shipment'"
        @click="actions.publishShipment()"
      >
        <Check />
        Publish
      </Button>
      <Button
        v-else-if="actions.canShowClose.value"
        size="small"
        severity="secondary"
        outlined
        :pt="actionButtonPt"
        v-tooltip.bottom="'Close shipment'"
        @click="actions.closeShipment()"
      >
        <TimesCircle />
        Close
      </Button>
      <Button
        v-if="actions.canManage.value && overflowItems.length"
        type="button"
        size="small"
        severity="secondary"
        outlined
        aria-haspopup="true"
        :aria-controls="overflowMenuId"
        aria-label="More actions"
        :pt="actionButtonPt"
        v-tooltip.bottom="'More actions'"
        @click="toggleOverflowMenu"
      >
        <template #icon>
          <EllipsisV />
        </template>
      </Button>
      <Menu :id="overflowMenuId" ref="menuRef" :model="overflowItems" popup>
        <template #itemicon>
          <Trash />
        </template>
      </Menu>
    </template>
  </div>
</template>
